import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Users, Users2, TreePine, HardHat, UserCheck, Crown, RefreshCw, X, Star, Zap, Droplets } from 'lucide-react';
import { Badge } from './ui/badge';
import { DraggableModal } from './ui/draggable-modal';
import ActiveDelegationsCard from './ActiveDelegationsCard';
import BlockedLoginDashboardCard from './BlockedLoginDashboardCard';
import { LawnMowerIcon, PaintBrushIcon, ChainsawIcon } from './icons/TeamIcons';
import { useRoleFlags } from '@/auth/useRoleFlags';
import {
  personnelStatGridLayout,
  personnelStatGridStyle,
  PS_GRID_CLASS,
  PS_LAST_MAIN_CLASS,
  PS_STATUS_CLASS,
  PS_STATUS_INNER_COLS,
} from './personnelStatsLayout';

// Dinamik ekip kartları için renk paleti (sırayla atanır). Tam class string'leri
// Tailwind purge'ü görsün diye literal yazıldı.
const TEAM_CARD_PALETTES = [
  { bg: 'bg-gradient-to-br from-teal-400 to-teal-500', border: 'border-teal-600', text: 'text-white', label: 'text-teal-50' },
  { bg: 'bg-gradient-to-br from-emerald-400 to-emerald-500', border: 'border-emerald-600', text: 'text-white', label: 'text-emerald-50' },
  { bg: 'bg-gradient-to-br from-green-400 to-green-500', border: 'border-green-600', text: 'text-white', label: 'text-green-50' },
  { bg: 'bg-gradient-to-br from-lime-400 to-lime-500', border: 'border-lime-600', text: 'text-white', label: 'text-lime-50' },
  { bg: 'bg-gradient-to-br from-sky-400 to-sky-500', border: 'border-sky-600', text: 'text-white', label: 'text-sky-50' },
  { bg: 'bg-gradient-to-br from-rose-400 to-rose-500', border: 'border-rose-600', text: 'text-white', label: 'text-rose-50' },
];

// Ana kart renk tonları — mevcut renkler korunur (kullanıcı kararı); yalnız
// diziliş ve kart biçimi Ana Sayfa istatistik kartlarına uyarlandı.
const MAIN_TONES = {
  toplam: { bg: 'bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200', icon: 'text-slate-600', value: 'text-slate-700', label: 'text-slate-600' },
  parkGorevlisi: { bg: 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200', icon: 'text-purple-600', value: 'text-purple-700', label: 'text-purple-600' },
  formen: { bg: 'bg-gradient-to-br from-yellow-400 to-yellow-500 border-yellow-600', icon: 'text-white', value: 'text-white', label: 'text-yellow-50' },
  bolgeMuhendisi: { bg: 'bg-gradient-to-br from-blue-400 to-blue-500 border-blue-600', icon: 'text-white', value: 'text-white', label: 'text-blue-50' },
  mudurYardimcisi: { bg: 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200', icon: 'text-orange-600', value: 'text-orange-700', label: 'text-orange-600' },
};

// Ekip ikonları — slug'a göre eşleştirilir; bilinmeyen ekipler için ad içindeki
// Türkçe anahtar kelimeyle yedek eşleştirme, son çare genel "Users2" ikonu.
// Çim/Boya/Budama özel çizilmiş SVG ikonlar (TeamIcons).
const TEAM_ICONS_BY_SLUG = {
  cim: LawnMowerIcon,
  budama: ChainsawIcon,
  boya: PaintBrushIcon,
  elektrik: Zap,
  sulama: Droplets,
};

function getTeamIcon(team) {
  const slug = String(team?.slug || '').toLowerCase();
  if (TEAM_ICONS_BY_SLUG[slug]) return TEAM_ICONS_BY_SLUG[slug];
  const name = String(team?.display_name || '').toLocaleLowerCase('tr-TR');
  if (name.includes('çim') || name.includes('cim')) return LawnMowerIcon;
  if (name.includes('budama')) return ChainsawIcon;
  if (name.includes('boya')) return PaintBrushIcon;
  if (name.includes('elektrik')) return Zap;
  if (name.includes('sulama')) return Droplets;
  return Users2;
}

const PersonnelStatisticsCards = ({
  personnel,
  allPersonnel, // Tüm personel listesi (filtreleme için)
  defaultTeamId, // Default "Park Görevlisi" ekibinin id'si
  teamCards = [], // Default olmayan aktif ekipler — üyesi olanlar kart olur
  userRole,
  personnelId,
  allActiveSubstitutions = [], // Aktif vekaletler
  regions = [],
  parks = [],
  leaveRequests = [], // İzin talepleri (izin türünü göstermek için)
  onFilterByRole, // Tıklama callback'i
  loginBlockedPersonnel = [],
  onUnblockPersonnelLogin,
  onEditPersonnel,
  unblockLoginLoadingId,
}) => {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;

  const [vekaletDetailOpen, setVekaletDetailOpen] = useState(false);
  // Ana sayfadaki Aktif Vekalet kartı ile aynı mantık: aynı (leave_request_id, asil) için tek vekalet say
  const dedupedActiveSubstitutions = React.useMemo(() => {
    const seen = new Set();
    return (allActiveSubstitutions || []).filter((sub) => {
      const key = `${sub?.leave_request_id || ''}_${sub?.original_personnel_id || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [allActiveSubstitutions]);
  const activeVekaletCount = dedupedActiveSubstitutions.length;
  // App tarafında rol kapsamı zaten hesaplanıp bu componente gelir.
  // Böylece kart sayıları ile liste aynı kişileri referans alır.
  // Pasif/ayrılmış personel SAYIMLARA dahil edilmez (yalnız aktif kadro sayılır).
  const isPassiveStatus = (p) =>
    ['passive', 'inactive', 'ayrildi', 'deleted'].includes(String(p?.status || '').toLowerCase());
  const myPersonnel = (Array.isArray(personnel) ? personnel : []).filter(p => !isPassiveStatus(p));
  
  // Özel Rol: müdürün atadığı, açıklamalı özel rol (App.js filtresiyle birebir aynı mantık).
  // custom_role_name eski import etiketleriyle de dolu; gerçek özel roller açıklama içerir.
  const hasOzelRol = (p) =>
    Boolean(
      p.custom_role_name && String(p.custom_role_name).trim() &&
      p.custom_role_description && String(p.custom_role_description).trim()
    );
  // Default dışı ekip üyesi (Çim/Sulama vb.) — saf park görevlisi sayımından düşülür.
  const isNonDefaultTeam = (p) =>
    Boolean(p.primary_team_id && defaultTeamId && String(p.primary_team_id) !== String(defaultTeamId));
  const ozelRolCount = myPersonnel.filter(hasOzelRol).length;
  const counts = {
    total: myPersonnel.length,
    // Saf park görevlisi: özel rolü olmayan ve default dışı ekipte olmayanlar
    // (Çim ekibi ve özel rol kartlarıyla çakışmaması için ayrıştırılır).
    park_gorevlisi: myPersonnel.filter(p =>
      p.role === 'park_gorevlisi' && !isNonDefaultTeam(p) && !hasOzelRol(p)
    ).length,
    formen: myPersonnel.filter(p => p.role === 'formen').length,
    bolge_muhendisi: myPersonnel.filter(p => p.role === 'bolge_muhendisi').length,
    mudur_yardimcisi: myPersonnel.filter(p => p.role === 'mudur_yardimcisi').length,
    ozel_rol: ozelRolCount
  };

  // Kart tıklama handler
  const handleCardClick = (role) => {
    if (onFilterByRole) {
      onFilterByRole(role);
    }
  };

  // Ortak kart stili
  const cardClass = "cursor-pointer transition-transform hover:scale-105 hover:shadow-lg";
  const normalizeRole = (role) => String(role || "").trim().toLowerCase();
  const isRegionBasedRole = (role) =>
    ["formen", "bolge_muhendisi", "mudur_yardimcisi"].includes(normalizeRole(role));
  const getDelegationRegionNames = React.useCallback((sub) => {
    const names = [];
    const seen = new Set();
    const regionNameById = new Map(
      (Array.isArray(regions) ? regions : [])
        .map((r) => [String(r?.id || "").trim(), String(r?.name || "").trim()])
        .filter(([id, name]) => id && name)
    );
    const parkById = new Map(
      (Array.isArray(parks) ? parks : [])
        .map((p) => [String(p?.id || "").trim(), p])
        .filter(([id]) => id)
    );
    const addRegion = (rid) => {
      const key = String(rid || "").trim();
      if (!key || seen.has(key)) return;
      const regionName = regionNameById.get(key);
      if (!regionName) return;
      seen.add(key);
      names.push(regionName);
    };

    const originalId = String(sub?.original_personnel_id || "").trim();
    const originalPerson = (Array.isArray(allPersonnel || personnel) ? (allPersonnel || personnel) : []).find(
      (p) => String(p?.id || "").trim() === originalId
    );
    (Array.isArray(originalPerson?.region_ids) ? originalPerson.region_ids : []).forEach(addRegion);
    addRegion(originalPerson?.region_id);
    addRegion(originalPerson?.formen_region_id);

    if (originalId) {
      (Array.isArray(regions) ? regions : []).forEach((r) => {
        if (String(r?.assigned_bolge_muhendisi_id || "").trim() === originalId) addRegion(r?.id);
        const formenIds = Array.isArray(r?.assigned_formen_ids) ? r.assigned_formen_ids : [];
        if (formenIds.some((fid) => String(fid || "").trim() === originalId)) addRegion(r?.id);
      });
    }

    (Array.isArray(sub?.parks) ? sub.parks : []).forEach((sp) => {
      addRegion(sp?.region_id);
      const pid = String(sp?.id || sp?.park_id || "").trim();
      if (pid) addRegion(parkById.get(pid)?.region_id);
    });
    return names;
  }, [regions, parks, allPersonnel, personnel]);

  // ── Kartlar ──────────────────────────────────────────────────────────────
  // Yerleşim Ana Sayfa istatistik kartlarıyla aynı (bkz. personnelStatsLayout):
  // ana kartlar yatay (etiket, sayı, sağda ikon); Özel Rol / Aktif Vekalet /
  // Engellenen küçük kartları tek "durum hücresi"nde yan yana; son satırda
  // boşluk kalmaz. Görünürlük kuralları ve tıklama davranışı değişmedi.
  const withTooltip = (key, node, text) => (
    <TooltipProvider key={key}>
      <Tooltip>
        <TooltipTrigger asChild>{node}</TooltipTrigger>
        <TooltipContent>
          <p className="text-sm">{text}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const mainCards = [
    { key: 'all', tone: MAIN_TONES.toplam, Icon: Users, label: 'Toplam Personel', value: counts.total, onClick: () => handleCardClick('all') },
    { key: 'park_gorevlisi', tone: MAIN_TONES.parkGorevlisi, Icon: TreePine, label: 'Park Görevlisi', value: counts.park_gorevlisi, onClick: () => handleCardClick('park_gorevlisi') },
  ];
  // Dinamik ekip kartları — default olmayan, üyesi > 0 olan aktif ekipler.
  // Tıklayınca o ekibe atanmış personeli filtreler (team:<id>).
  teamCards.forEach((team, idx) => {
    const teamCount = myPersonnel.filter(
      p => String(p.primary_team_id || '') === String(team.id)
    ).length;
    if (teamCount === 0) return;
    const palette = TEAM_CARD_PALETTES[idx % TEAM_CARD_PALETTES.length];
    mainCards.push({
      key: `team:${team.id}`,
      tone: { bg: `${palette.bg} ${palette.border}`, icon: palette.text, value: palette.text, label: palette.label },
      Icon: getTeamIcon(team),
      label: team.display_name,
      value: teamCount,
      onClick: () => handleCardClick(`team:${team.id}`),
    });
  });
  // Formen - Formen ve park görevlisi görmez
  if (!isFormen && !isParkGorevlisi) {
    mainCards.push({ key: 'formen', tone: MAIN_TONES.formen, Icon: HardHat, label: 'Formen', value: counts.formen, onClick: () => handleCardClick('formen'), tooltip: 'Tıklayarak formenleri listeleyin' });
  }
  // Bölge Mühendisi - Sadece Müdür Yardımcısı ve Müdür görsün
  if (isAdmin || isMudur || isMudurYardimcisi) {
    mainCards.push({ key: 'bolge_muhendisi', tone: MAIN_TONES.bolgeMuhendisi, Icon: UserCheck, label: 'Bölge Mühendisi', value: counts.bolge_muhendisi, onClick: () => handleCardClick('bolge_muhendisi'), tooltip: 'Tıklayarak bölge mühendislerini listeleyin' });
  }
  // Müdür Yardımcısı - Sadece Admin görsün
  if (isAdmin || isMudur) {
    mainCards.push({ key: 'mudur_yardimcisi', tone: MAIN_TONES.mudurYardimcisi, Icon: Crown, label: 'Müdür Yardımcısı', value: counts.mudur_yardimcisi, onClick: () => handleCardClick('mudur_yardimcisi'), tooltip: 'Tıklayarak müdür yardımcılarını listeleyin' });
  }

  // Durum hücresi küçük kartları (dikey; satırın yüksekliğine uzar).
  const statusCards = [];
  // Özel Rol - Çim Ekibi hariç, standart dışı roller
  if (isAdmin || isMudur || isMudurYardimcisi || isBolgeMuhendisi || isFormen) {
    statusCards.push(withTooltip(
      'ozel_rol',
      <Card
        className={`bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-300 ${cardClass} h-full`}
        onClick={() => handleCardClick('ozel_rol')}
      >
        <CardContent className="h-full flex items-center justify-center p-4">
          <div className="flex flex-col items-center text-center">
            <Star className="h-8 w-8 text-cyan-600 mb-2" />
            <p className="text-2xl font-bold text-cyan-700">{counts.ozel_rol}</p>
            <p className="text-xs text-cyan-600 font-semibold">Özel Rol</p>
          </div>
        </CardContent>
      </Card>,
      'Çim Ekibi hariç; özel rol tanımlı personeli listeleyin'
    ));
  }
  if (isAdmin) {
    // Süper admin: Aktif Vekaletler + Engellenenler kendi modallarıyla.
    statusCards.push(
      <ActiveDelegationsCard
        key="aktif_vekalet"
        activeSubstitutions={allActiveSubstitutions}
        title="🔄 Aktif Vekaletler"
        className="h-full"
        personnel={allPersonnel || personnel}
        regions={regions}
        parks={parks}
      />
    );
    statusCards.push(
      <BlockedLoginDashboardCard
        key="engellenen"
        blockedPersonnel={loginBlockedPersonnel}
        onUnblock={onUnblockPersonnelLogin}
        onEditPerson={onEditPersonnel}
        unblockLoginLoadingId={unblockLoginLoadingId}
        className="h-full"
      />
    );
  } else if (activeVekaletCount > 0) {
    // Aktif Vekalet (sayı: ana sayfadaki kartla aynı - leave_request + asil bazında tek vekalet)
    statusCards.push(withTooltip(
      'aktif_vekalet',
      <Card
        className={`bg-gradient-to-br from-indigo-400 to-indigo-500 border-indigo-600 ${cardClass} h-full`}
        onClick={() => setVekaletDetailOpen(true)}
      >
        <CardContent className="h-full flex items-center justify-center p-4">
          <div className="flex flex-col items-center text-center">
            <RefreshCw className="h-8 w-8 text-white mb-2" />
            <p className="text-2xl font-bold text-white">{activeVekaletCount}</p>
            <p className="text-xs text-indigo-50 font-semibold">Aktif Vekalet</p>
          </div>
        </CardContent>
      </Card>,
      'Tıklayarak vekalet detaylarını görüntüleyin'
    ));
  }

  const gridLayout = personnelStatGridLayout(mainCards.length, statusCards.length);
  const gridStyle = personnelStatGridStyle(gridLayout);
  const renderMainCard = (c, extraClass) => (
    <Card
      key={c.tooltip ? undefined : c.key}
      className={`flex flex-col ${c.tone.bg} ${cardClass} ${extraClass}`}
      onClick={c.onClick}
    >
      {/* Alt kırılımı olmayan kart satırın yüksekliğine uzar; içerik dikeyde ortalı. */}
      <CardContent className="flex-1 flex items-center p-4">
        <div className="flex w-full items-center justify-between gap-2">
          <div className="min-w-0">
            <p className={`${c.tone.label} text-[13px] font-medium truncate`}>{c.label}</p>
            <h3 className={`${c.tone.value} text-2xl font-bold mt-0.5`}>{c.value}</h3>
          </div>
          <c.Icon className={`h-7 w-7 ${c.tone.icon} opacity-80 shrink-0`} />
        </div>
      </CardContent>
    </Card>
  );

  return (
    <>
    <div className={PS_GRID_CLASS} style={gridStyle}>
      {mainCards.map((c, i) => {
        const card = renderMainCard(c, i === mainCards.length - 1 ? PS_LAST_MAIN_CLASS : '');
        return c.tooltip ? withTooltip(c.key, card, c.tooltip) : card;
      })}
      {statusCards.length > 0 && (
        <div className={`${PS_STATUS_CLASS} grid gap-4 ${PS_STATUS_INNER_COLS[statusCards.length] || 'grid-cols-3'}`}>
          {statusCards}
        </div>
      )}
    </div>

      {/* Vekalet Detay Modal (süper admin ActiveDelegationsCard modalını kullanır) */}
      {!isAdmin && (
      <DraggableModal
        isOpen={vekaletDetailOpen}
        onClose={() => setVekaletDetailOpen(false)}
        title="🔄 Aktif Vekaletler"
        subtitle={`Toplam ${activeVekaletCount} aktif vekalet`}
        maxWidth="max-w-3xl"
      >
        <div className="space-y-4">
          {dedupedActiveSubstitutions
            .slice()
            .sort((a, b) => (a.original_personnel_name || '').localeCompare(b.original_personnel_name || '', 'tr'))
            .map(sub => {
              const originalPerson = (allPersonnel || personnel).find(p => p.id === sub.original_personnel_id);
              const substitutePerson = (allPersonnel || personnel).find(p => p.id === sub.substitute_personnel_id);
              const showRegions = isRegionBasedRole(originalPerson?.role);
              const regionNames = getDelegationRegionNames(sub);
              // Çöktürülmüş kayıtta üst-seviye substitute_personnel_name yalnız "ilk
              // dolu vekil"dir. Park başına gerçek vekili kırılımdan (substitute_breakdown)
              // çöz. Birden çok farklı vekil varsa park rozetlerinde "park → vekil" göster.
              const subBreakdown = Array.isArray(sub?.substitute_breakdown) ? sub.substitute_breakdown : [];
              const parkVekilById = new Map(
                subBreakdown
                  .filter((b) => (b?.id || b?.park_id) && b?.substitute_personnel_name)
                  .map((b) => [b.id || b.park_id, b.substitute_personnel_name])
              );
              const distinctSubNames = new Set(
                subBreakdown
                  .map((b) => String(b?.substitute_personnel_id || b?.substitute_personnel_name || "").trim())
                  .filter(Boolean)
              );
              const isPerParkSub = !showRegions && distinctSubNames.size > 1;

              // İzin türünü bul - önce substitution'dan, yoksa leave request'ten
              const leaveType = sub.reason_type || (leaveRequests.find(lr => lr.id === sub.leave_request_id)?.reason_type) || 'İzin';
              
              // İzin türüne göre renk şeması
              const getLeaveTypeStyle = (type) => {
                switch(type) {
                  case 'Yıllık İzin':
                    return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300', icon: '🔵' };
                  case 'Rapor':
                    return { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300', icon: '🩺' };
                  case 'Sosyal İzin':
                    return { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300', icon: '🟣' };
                  case 'Diğer':
                    return { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300', icon: '⚪' };
                  default:
                    return { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', icon: '📋' };
                }
              };
              
              const leaveStyle = getLeaveTypeStyle(leaveType);
              
              // Kart arka plan rengi izin türüne göre
              const getCardBgStyle = (type) => {
                switch(type) {
                  case 'Yıllık İzin':
                    return { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', label: 'İZİNDE' };
                  case 'Rapor':
                    return { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700', label: 'RAPORLU' };
                  case 'Sosyal İzin':
                    return { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', label: 'SOSYAL İZİNDE' };
                  case 'Diğer':
                    return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', label: 'İZİNDE' };
                  default:
                    return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', label: 'İZİNDE' };
                }
              };
              
              const cardStyle = getCardBgStyle(leaveType);
              
              return (
                <Card key={sub.id} className={`border-l-4 ${leaveType === 'Yıllık İzin' ? 'border-l-blue-500' : leaveType === 'Rapor' ? 'border-l-pink-500' : leaveType === 'Sosyal İzin' ? 'border-l-purple-500' : 'border-l-gray-500'}`}>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Sol: İzindeki Personel - izin türüne göre renk */}
                      <div className={`${cardStyle.bg} rounded-lg p-3 border ${cardStyle.border}`}>
                        <div className="flex items-center justify-between mb-1">
                          <p className={`text-xs ${cardStyle.text} font-semibold`}>{cardStyle.label}</p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${leaveStyle.bg} ${leaveStyle.text} ${leaveStyle.border} border font-semibold`}>
                            {leaveStyle.icon} {leaveType}
                          </span>
                        </div>
                        <p className={`font-bold ${cardStyle.text}`}>{originalPerson?.name || sub.original_personnel_name || '-'}</p>
                        {originalPerson && (
                          <p className={`text-xs ${cardStyle.text} opacity-80`}>
                            {originalPerson.role === 'park_gorevlisi' ? 'Park Görevlisi' : 
                             originalPerson.role === 'çim_ekibi' ? 'Çim Ekibi' : originalPerson.role}
                          </p>
                        )}
                        <div className={`mt-2 flex items-center gap-2 text-xs ${cardStyle.text} opacity-80`}>
                          <span>📅</span>
                          <span>{new Date(sub.start_date).toLocaleDateString('tr-TR')} - {new Date(sub.end_date).toLocaleDateString('tr-TR')}</span>
                        </div>
                      </div>
                      
                      {/* Sağ: Vekil - Turuncu tonu (VEKALETEN rengi ile uyumlu) */}
                      <div className="bg-orange-50/70 rounded-lg p-3 border border-orange-300">
                        <p className="text-xs text-orange-500 font-semibold mb-1">VEKİL</p>
                        {isPerParkSub ? (
                          <p className="font-bold text-orange-700">Park bazında farklı ({distinctSubNames.size} vekil)</p>
                        ) : (
                          <>
                            <p className="font-bold text-orange-700">{substitutePerson?.name || sub.substitute_personnel_name || '-'}</p>
                            {substitutePerson && (
                              <p className="text-xs text-orange-500">
                                {substitutePerson.role === 'park_gorevlisi' ? 'Park Görevlisi' :
                                 substitutePerson.role === 'çim_ekibi' ? 'Çim Ekibi' : substitutePerson.role}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Vekalet kapsamı: yönetici rollerde bölge, diğerlerinde park */}
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-gray-600 mb-2">
                        {showRegions ? "Vekalet Bölgeleri:" : "Vekalet Parkları:"}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {showRegions
                          ? (regionNames.length > 0 ? regionNames : ["Bölge bilgisi bulunamadı"]).map((name) => (
                              <Badge key={name} variant="outline" className="text-xs bg-blue-50 border-blue-300 text-blue-700">
                                {name}
                              </Badge>
                            ))
                          : (sub.parks || []).map(park => {
                              const parkVekil = parkVekilById.get(park.id || park.park_id);
                              return (
                                <Badge key={park.id} variant="outline" className="text-xs bg-green-50 border-green-300 text-green-700">
                                  {park.name}
                                  {isPerParkSub && parkVekil ? ` → ${parkVekil}` : ""}
                                </Badge>
                              );
                            })}
                      </div>
                    </div>
                    
                    {/* Önceki Vekil Bilgisi (transfer olduysa) */}
                    {sub.previous_substitute_name && (
                      <div className="mt-2 text-xs text-gray-500 italic">
                        ⚠️ Önceki vekil: {sub.previous_substitute_name}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          
          {activeVekaletCount === 0 && (
            <p className="text-center text-gray-500 py-8">Aktif vekalet bulunmuyor</p>
          )}
        </div>
      </DraggableModal>
      )}
    </>
  );
};

export default PersonnelStatisticsCards;
